<template>
  <v-dialog v-model="shown" :width="720" :persistent="true" @input="maybeDismiss">
    <v-card v-if="shown">
      <v-card-title class="py-2">
        <span class="headline">{{ isNew ? 'New Stage' : 'Edit Stage' }}</span>
        <v-spacer/>
        <v-chip small :color="stage.kind === 'code' ? 'info' : 'success'" text-color="white" class="ma-0">
          {{ stage.kind === 'code' ? 'CODE' : 'LLM' }}
        </v-chip>
      </v-card-title>
      <v-divider/>

      <div class="pa-3">
        <div class="c-d-flex c-align-center">
          <div style="width: 40%" class="pr-2">
            <v-text-field
                dense hide-details single-line
                v-model="stage.id"
                label="Stage ID (referenced as stages.[id].output)"
            />
          </div>
          <div style="width: 60%">
            <v-text-field
                dense hide-details single-line
                v-model="stage.label"
                label="Display Label"
            />
          </div>
        </div>

        <div v-if="stage.kind === 'llm'">
          <div class="c-d-flex c-align-center pt-3">
            <div style="width: 33%" class="pr-2">
              <v-autocomplete
                  dense hide-details single-line
                  v-model="stage.iterate"
                  :items="iterateOptions"
                  item-text="description" item-value="id"
                  label="Iteration"
              />
            </div>
            <div style="width: 34%" class="pr-2">
              <v-autocomplete
                  dense hide-details single-line
                  v-model="stage.model"
                  :items="models"
                  item-text="name" item-value="id"
                  label="Model (blank = field default)"
                  clearable
              />
            </div>
            <div style="width: 33%">
              <v-checkbox
                  dense hide-details
                  v-model="stage.outputJson"
                  label="Parse response as JSON"
                  class="pa-0 ma-0"
              />
            </div>
          </div>

          <div class="c-d-flex c-align-center pt-3">
            <div style="width: 33%" class="pr-2">
              <v-text-field
                  dense hide-details single-line type="number"
                  v-model.number="stage.maxTokens"
                  label="Max Tokens"
              />
            </div>
            <div style="width: 33%" class="pr-2">
              <v-text-field
                  dense hide-details single-line type="number"
                  v-model.number="stage.maxThinkingTokens"
                  label="Max Thinking Tokens"
              />
            </div>
            <div style="width: 34%">
              <v-text-field
                  dense hide-details single-line type="number"
                  v-model.number="retryAttempts"
                  label="Retry Max Attempts"
              />
            </div>
          </div>

          <div class="pt-3">
            <div class="caption grey--text">System Prompt</div>
            <div class="preview-box" :class="isDarkMode ? 'dark' : 'light'">
              {{ truncate(stage.systemPrompt) || 'Not set' }}
            </div>
            <v-btn small flat color="secondary" class="ma-0 mt-1" @click="editing = 'systemPrompt'">Edit System Prompt</v-btn>
          </div>

          <div class="pt-3">
            <div class="caption grey--text">
              Prompt (supports
              <code v-pre>{{doc}}</code>,
              <code v-pre>{{docIndex}}</code>,
              <code v-pre>{{stages.X.output}}</code>,
              <code v-pre>{{retry.lastError}}</code>)
            </div>
            <div class="preview-box" :class="isDarkMode ? 'dark' : 'light'">
              {{ truncate(stage.prompt) || 'Not set' }}
            </div>
            <v-btn small flat color="secondary" class="ma-0 mt-1" @click="editing = 'prompt'">Edit Prompt</v-btn>
          </div>

          <div class="pt-3">
            <div class="caption grey--text">Validator (JS body — receives <code>output</code>, <code>doc</code>, <code>docIndex</code>, <code>stages</code>; return <code>{ ok: false, retryReason: '...' }</code> to retry)</div>
            <div class="preview-box" :class="isDarkMode ? 'dark' : 'light'">
              {{ truncate(stage.validator) || 'Not set (no validation)' }}
            </div>
            <v-btn small flat color="secondary" class="ma-0 mt-1" @click="editing = 'validator'">Edit Validator</v-btn>
          </div>
        </div>

        <div v-else-if="stage.kind === 'code'">
          <div class="pt-3">
            <div class="caption grey--text">
              Code (receives <code>stages</code>, <code>doc</code>, <code>docIndex</code>, <code>docs</code>,
              <code>getTaskHeader</code>, <code>getTaskStepsAndValues</code>, <code>getDoc</code>, <code>getDocs</code>, <code>getValue</code>)
            </div>
            <div class="preview-box" :class="isDarkMode ? 'dark' : 'light'">
              {{ truncate(stage.code) || 'Not set' }}
            </div>
            <v-btn small flat color="secondary" class="ma-0 mt-1" @click="editing = 'code'">Edit Code</v-btn>
          </div>
        </div>
      </div>

      <v-divider/>
      <div class="py-2 pr-2 c-d-flex c-justify-end">
        <v-btn small flat class="ma-0 mr-2" @click="$emit('dismiss')">Cancel</v-btn>
        <v-btn small color="secondary" class="ma-0" @click="save">Save Stage</v-btn>
      </div>
    </v-card>

    <a-i-prompt-editor
        v-if="editing === 'systemPrompt'"
        :shown="true"
        v-model="stage.systemPrompt"
        title="System Prompt"
        @dismiss="editing = null"
    />
    <a-i-prompt-editor
        v-if="editing === 'prompt'"
        :shown="true"
        v-model="stage.prompt"
        title="Prompt"
        @dismiss="editing = null"
    />
    <a-i-prompt-editor
        v-if="editing === 'validator'"
        :shown="true"
        v-model="stage.validator"
        title="Validator JS"
        @dismiss="editing = null"
    />
    <a-i-prompt-editor
        v-if="editing === 'code'"
        :shown="true"
        v-model="stage.code"
        title="Stage Code"
        @dismiss="editing = null"
    />
  </v-dialog>
</template>

<script>
import AIPromptEditor from "./AIPromptEditor";

export default {
  components: { AIPromptEditor },
  props: {
    value: { type: Object, required: true },
    shown: { type: Boolean, default: false },
    models: { type: Array, default: () => [] },
    isNew: { type: Boolean, default: false },
  },
  data() {
    return {
      stage: this.cloneStage(this.value),
      editing: null,
    }
  },
  watch: {
    value: {
      immediate: false,
      handler(v) { this.stage = this.cloneStage(v) }
    },
    shown(v) { if (v) this.stage = this.cloneStage(this.value) }
  },
  computed: {
    isDarkMode() { return this.$store.state.darkTheme },
    iterateOptions() {
      return [
        { id: 'none', description: 'Single call (all docs attached)' },
        { id: 'perDoc', description: 'Per document (parallel fan-out)' },
      ]
    },
    retryAttempts: {
      get() { return (this.stage.retry && this.stage.retry.maxAttempts) || 1 },
      set(v) { this.$set(this.stage, 'retry', { maxAttempts: Math.max(1, Number(v) || 1) }) }
    }
  },
  methods: {
    cloneStage(v) {
      const base = {
        id: '', label: '', kind: 'llm', iterate: 'none',
        systemPrompt: '', prompt: '', model: null,
        maxTokens: 4000, maxThinkingTokens: null, outputJson: false,
        validator: '', retry: { maxAttempts: 1 }, code: '',
      }
      return Object.assign({}, base, JSON.parse(JSON.stringify(v || {})))
    },
    truncate(s) {
      if (!s) return ''
      const t = String(s).replace(/\s+/g, ' ').trim()
      return t.length > 160 ? t.slice(0, 160) + '…' : t
    },
    maybeDismiss(v) { if (!v) this.$emit('dismiss') },
    save() {
      if (!this.stage.id || !this.stage.id.trim()) {
        this.$store.commit('showSnack', { text: 'Stage ID is required', color: 'error', timeout: 3000 })
        return
      }
      this.$emit('input', this.stage)
      this.$emit('save', this.stage)
    }
  }
}
</script>

<style scoped>
.preview-box {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  padding: 6px 8px;
  border-radius: 3px;
  min-height: 28px;
  max-height: 60px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.preview-box.dark { background: rgba(50,50,50,0.5); color: rgba(255,255,255,0.87); border: 1px solid rgba(255,255,255,0.12); }
.preview-box.light { background: rgba(0,0,0,0.06); color: rgba(0,0,0,0.87); border: 1px solid rgba(0,0,0,0.08); }
</style>
